import type { ResellerPermission, ResellerRoleKey } from "./resellerPermissions.js";

/**
 * The reseller identity a signed-in session carries, per
 * `docs/superpowers/specs/2026-08-28-reseller-identity-and-scope-wall-design.md`
 * §3.3 (reseller memberships) and §5 (auth flow).
 *
 * Assembled by the BFF from TWO upstreams, neither of which knows the
 * whole story: portal-api supplies the human (`email`, `name`), crm-api
 * supplies the company and the membership role. See
 * `apps/server/src/routes/auth.ts`.
 */

export interface SessionUser {
  email: string;
  /** Display name from portal-api. Falls back to the email when the
   * account has no name set, so the UI never renders an empty chip. */
  name: string;
  resellerId: number;
  resellerName: string;
  /** A built-in role or one of the company's custom roles (2026-09-15). */
  role: ResellerRoleKey;
  /** crm-api's name for `role` at sign-in. Absent from sessions minted
   * before custom roles, and from a crm-api that does not send it — render
   * through `roleLabel`, which falls back to the built-in name. */
  roleLabel?: string;
}

/**
 * A successful `POST /api/auth/login`.
 *
 * TWO outcomes share the 200. A reseller with a live membership gets `user`.
 * An APPLICANT — a `role: "reseller"` account with no membership but an open
 * reseller application (spec E §6.2, gate 3b) — gets `applicant` and a null
 * `user`: the BFF minted an applicant cookie that reaches only the onboarding
 * wall, and the SPA routes on which of the two is present.
 */
export interface LoginResponse {
  user: SessionUser | null;
  applicant?: ApplicantSession;
  /** The effective permission set (spec F §6.1). Present with `user`. */
  permissions?: ResellerPermission[];
}

/** Where a reseller application is in its life (spec E §4.1). */
export type OnboardingStatusValue =
  | "submitted"
  | "documents_submitted"
  | "active"
  | "rejected";

/**
 * Who an APPLICANT cookie names. Deliberately carries no reseller id — there
 * is none yet — and is a different token `type` from `SessionUser`'s cookie,
 * so `requireSession` refuses it by construction (spec E §4.3).
 *
 * `status` is the application's status as of the last time this BFF looked
 * (sign-in, or the last `/api/onboarding/status` poll, which re-mints the
 * cookie when it changes). It is for ROUTING — applicant goes to the wall —
 * never for rendering: the wall reads the live status itself.
 */
export interface ApplicantSession {
  email: string;
  name: string;
  applicationId: number;
  status: OnboardingStatusValue;
}

/** One of the applicant's two uploads, as `GET /api/onboarding/status` reports it. */
export interface DocumentSlot {
  attached: boolean;
  /** The server-assigned name (`signed_agreement-<id>.pdf`), never the upload's. */
  filename: string | null;
  uploadedAt: string | null;
}

/**
 * `GET /api/onboarding/status` and the body of a successful
 * `POST /api/onboarding/documents`. A projection of the applicant's OWN
 * application row — never the match candidates, never another reseller.
 *
 * `user` is present on exactly one answer: the poll that found the
 * application `active` AND re-ran gate 3 successfully. The same response
 * carries the re-minted full session cookie (spec E §6.3), so the SPA calls
 * `refresh()` and the inner portal appears without a re-login.
 */
export interface OnboardingStatus {
  status: OnboardingStatusValue;
  companyName: string;
  documents: {
    signedAgreement: DocumentSlot;
    signedNda: DocumentSlot;
  };
  documentsSubmittedAt: string | null;
  decidedAt: string | null;
  /** Set only when `status` is `rejected`. */
  decisionNote: string | null;
  user?: SessionUser;
}

/** `POST /api/register`. Optional fields are OMITTED when blank, never sent
 * as empty strings, so the CRM row does not fill with `""`. */
export interface RegisterInput {
  companyName: string;
  website?: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  country?: string;
  message?: string;
}

/**
 * `POST /api/auth/login` when the account has 2FA enrolled. portal-api
 * answers the password step with a challenge rather than tokens, and the
 * BFF passes that through so the SPA can prompt for the code and re-post
 * the same credentials with `otp` attached.
 *
 * Deliberately NOT an error shape: it is a normal, successful step in the
 * flow, and modelling it as a 4xx would have the SPA rendering "sign-in
 * failed" for a healthy account.
 */
export interface TwoFactorChallenge {
  twoFactorRequired: true;
  /** Which factor to prompt for. `email` means a code was just sent. */
  method: "totp" | "email";
  /** The address an emailed code went to, for the prompt copy. Null for
   * TOTP, where there is nothing to disclose. */
  emailHint: string | null;
}

export type LoginResult = LoginResponse | TwoFactorChallenge;

export function isTwoFactorChallenge(
  result: LoginResult,
): result is TwoFactorChallenge {
  return (result as TwoFactorChallenge).twoFactorRequired === true;
}

/**
 * `GET /api/auth/invitation/:token` — what the accept screen shows before
 * asking for a password.
 *
 * Only the invitee's own email comes back. The reseller company's name is
 * NOT here on purpose: the token arrives from an email inbox, and echoing
 * "you are joining <Company>" to whoever holds the link turns the endpoint
 * into a lookup for which companies XOP resells through.
 */
export interface InvitationPreview {
  email: string;
  /** ISO-8601, or null when the invite carries no expiry. */
  expiresAt: string | null;
}

/**
 * `POST /api/auth/accept-invitation`.
 *
 * `signedIn` is separate from success on purpose. Setting the password can
 * succeed while the sign-in that follows it does not — a membership revoked
 * between invite and accept, or the CRM being briefly unreachable. The
 * password is saved either way, so the screen must say so and point at the
 * sign-in page rather than reporting a failure that would have the invitee
 * try to accept a now-burned token again.
 */
export interface AcceptInvitationResponse {
  signedIn: boolean;
  user: SessionUser | null;
  /** A registrant whose link this was: the password is set AND an applicant
   * session was minted, so the SPA sends them to the onboarding wall. */
  applicant?: ApplicantSession;
  /** Why sign-in did not complete. Only set when `signedIn` is false. */
  message?: string;
  /** Effective permissions, present with a signed-in `user` (spec F §6.1). */
  permissions?: ResellerPermission[];
}

/** `GET /api/auth/me`. Both null when nobody is signed in — that is the
 * normal signed-out answer, not an error. At most one is set. */
export interface MeResponse {
  user: SessionUser | null;
  applicant: ApplicantSession | null;
  /** Effective permissions for `user`; empty when nobody is signed in. Read
   * through a 60-second cache in the BFF — rendering only, never a gate. */
  permissions: ResellerPermission[];
}

/** Standard error body every BFF route returns on failure. */
export interface ApiErrorBody {
  error: string;
  code: string;
  message: string;
  /**
   * Which fields were refused and why, keyed by the field name the client
   * sent — present only where a route can answer that precisely.
   *
   * `message` alone is what a form used to get, and on a page with twenty-two
   * fields it was not enough to act on: a single sentence naming three rules
   * left the reseller checking fields that were fine. A client that gets this
   * marks the field; one that does not still has `message`.
   */
  fields?: Record<string, string>;
  /** Structured facts about a refusal, forwarded from crm-api where it sends
   * them — e.g. `{ member_count }` on a `ROLE_IN_USE` 409, so the page can say
   * how many people still hold the role. */
  details?: Record<string, unknown>;
}

/** One row of the reseller's customer list. A projection of crm-api's
 * customer payload — only the fields the portal renders, so a change to
 * the CRM's serializer cannot silently widen what this app exposes. */
export interface ResellerCustomer {
  id: number;
  name: string;
  city: string | null;
  state: string | null;
  status: string | null;
  createdAt: string | null;
}

/** One row of the reseller's invoice list. */
export interface ResellerInvoice {
  id: number;
  number: string | null;
  customerName: string | null;
  status: string | null;
  total: number | null;
  currency: string | null;
  issuedOn: string | null;
  dueOn: string | null;
}

/** One agreement coming up for renewal, as the overview lists it.
 *
 * Deliberately carries NO money. The customers list applies the same rule
 * and says why: per-customer value is not something this portal puts on a
 * screen. The aggregate totals on the overview are the reseller's own book
 * in one number, which is a different thing from pricing a named account. */
export interface ResellerRenewal {
  customerId: number;
  customerName: string;
  agreementId: number;
  title: string | null;
  /** The date this was sorted and filtered on — `renewal_date` when the CRM
   * has one, otherwise the agreement's end date. */
  renewsOn: string;
}

/** The dashboard's headline numbers. Every field is nullable because
 * crm-api may legitimately answer without one (an empty subtree has no
 * totals), and rendering "—" is better than rendering a fabricated 0.
 *
 * THE LISTS ARE NULLABLE FOR THE SAME REASON, and the distinction carries
 * real meaning: `[]` is "nothing is overdue", which is good news worth
 * showing; `null` is "we could not ask", which is not news at all. Collapsing
 * the two would let a failed upstream congratulate a reseller on being paid
 * up. */
export interface ResellerOverview {
  customerCount: number | null;
  /** Leads XOP's salespeople have attached to this reseller — the count
   * behind the rail's Prospects chip (spec E §10.3). */
  prospectCount: number | null;
  invoiceCount: number | null;
  invoiceTotal: number | null;
  outstandingTotal: number | null;
  currency: string | null;
  /** OPEN quotes XOP Networks has issued TO this reseller — the count behind
   * the overview's Quotes tile (spec 2026-09-09 §5). NOT the reseller's own
   * customer-facing quotes: that section no longer exists in this portal, and
   * this is the only quote figure with an action behind it. */
  quoteCount: number | null;
  paidInvoiceCount: number | null;
  unpaidInvoiceCount: number | null;
  overdueInvoiceCount: number | null;
  overdueTotal: number | null;
  /** The oldest few overdue invoices — what to chase first. */
  overdueInvoices: ResellerInvoice[] | null;
  /** Agreements renewing inside the next 60 days, soonest first. */
  renewals: ResellerRenewal[] | null;
}

/** A customer's full record, as the detail page and the edit form use it.
 * A superset of {@link ResellerCustomer}, which is the list projection. */
export interface ResellerCustomerDetail extends ResellerCustomer {
  companyName: string | null;
  contactName: string | null;
  email: string | null;
  phone: string | null;
  website: string | null;
  address: string | null;
  postalCode: string | null;
  country: string | null;
  /** The licence/agreement rows — start, end, renewal, product feature.
   * crm-api calls these `customer_deals`; the portal calls them agreements,
   * because that is what a reseller calls the thing it sells. */
  agreements: ResellerAgreement[];
  contacts: ResellerContact[];
}

/** One AGREEMENT on a customer — what it bought.
 *
 * Distinct from a LICENCE (`ResellerLicence` in ./licence.ts), which is one
 * APPLIANCE and what that box may run. A customer with two servers splits its
 * purchased capacity between them, so the two carry different numbers on
 * purpose. */
export interface ResellerAgreement {
  id: number;
  title: string | null;
  amount: number | null;
  dealType: string | null;
  startsOn: string | null;
  endsOn: string | null;
  renewsOn: string | null;
  /** Derived from `endsOn` by `licenceStatus`, so every surface says the same
   * thing about whether this AGREEMENT is live. `unknown` is right here: an
   * agreement with no recorded end date is a gap in the data, not a promise of
   * perpetuity — which is exactly what it DOES mean on a licence. */
  status: "active" | "expiring" | "expired" | "unknown";
}

export interface ResellerContact {
  id: number;
  name: string | null;
  email: string | null;
  phone: string | null;
  title: string | null;
}

/** What a customer create/edit form sends. Every field optional so the form
 * can PATCH just what changed. */
export interface CustomerInput {
  companyName?: string;
  city?: string;
  state?: string;
  address?: string;
  postalCode?: string;
  country?: string;
  email?: string;
  phone?: string;
  website?: string;
}

/** One line on an invoice or quote. */
export interface DocumentLine {
  description: string;
  seats: number;
  chargePerSeat: number;
}

/** What the invoice/quote builder sends. */
export interface DocumentInput {
  customerId: number;
  documentType: "invoice" | "quote";
  issueDate: string;
  dueDate: string;
  subject?: string;
  poNumber?: string;
  terms?: string;
  lines: DocumentLine[];
}

/** A document with everything needed to RENDER it — the lines, and the trade
 * identity it is issued under. */
export interface ResellerDocumentDetail extends ResellerInvoice {
  documentType: "invoice" | "quote";
  subject: string | null;
  poNumber: string | null;
  terms: string | null;
  subTotal: number | null;
  balanceDue: number | null;
  lines: DocumentLine[];
  customerId: number | null;
  /** Who the document is billed FROM. Absent on an XOP-issued document,
   * which a reseller never sees anyway. */
  issuedBy: ResellerBranding | null;
}

/**
 * The half of a trade identity the BOTTOM of a document is built from: where
 * the money goes, and who answers questions about it.
 *
 * These are the fields crm-api prints at the foot of a document it renders
 * (`InvoicePdfRenderer`, the one generator in the stack — the portal draws no
 * document itself since 2026-09-17). Kept as a named interface because
 * `ResellerBranding` is built from it.
 */
export interface IssuerPaymentIdentity {
  paymentTerms: string | null;
  /** Anything the bank fields have no box for — Zelle, PayPal, a payment
   * link, a note about reference numbers. */
  paymentInstructions: string | null;
  invoiceFooter: string | null;
  billingEmail: string | null;
  billingPhone: string | null;
  /** The address on the "For questions…" strip. Falls back to
   * `billingEmail` — the address that chases payment and the one that
   * answers questions are often, but not always, the same. */
  documentContactEmail: string | null;
  bankName: string | null;
  bankAccountName: string | null;
  bankAccountNumber: string | null;
  bankIban: string | null;
  bankSwift: string | null;
  bankRoutingNumber: string | null;
}

/** A reseller's own trade identity — what its documents are issued under.
 * `resellerName` is XOP's label for the company and is READ-ONLY; everything
 * else the reseller controls. */
export interface ResellerBranding extends IssuerPaymentIdentity {
  resellerName: string;
  /** XOP's three-digit code for this company, and the first segment of every
   * document number it issues (`901-0011-001`). READ-ONLY, like
   * `resellerName` — crm-api assigns it and refuses a change from here. It
   * travels with the branding so the Settings preview can show a sample
   * number that starts with the same digits the real ones will. */
  resellerCode: string | null;
  legalName: string | null;
  taxId: string | null;
  currency: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  country: string | null;
  website: string | null;
  /** Accent colour on the document — "#rrggbb", or null for the generator's
   * default. crm-api refuses anything that is not a hex triple, because this
   * value is written into a PDF as a colour literal. */
  brandColor: string | null;
  logoAttached: boolean;
}

/** The name a document is issued under: the legal name if the reseller set
 * one, otherwise the name XOP knows them by. One function so no surface
 * disagrees about it. */
export function issuerDisplayName(b: ResellerBranding): string {
  return b.legalName?.trim() || b.resellerName;
}

/* ────────────────────────────────────────────────────────────────────────
 * Spec D — account, team and network
 * ──────────────────────────────────────────────────────────────────────── */

/**
 * `GET /api/profile` — the signed-in person, assembled from BOTH boxes
 * exactly as `SessionUser` is, because neither knows the whole story.
 *
 * portal-api owns the human (name, password, second factor); crm-api owns the
 * authorisation (which reseller, what role). They are kept as separate nested
 * objects rather than flattened so it stays obvious which box answers for
 * which field — and so a reader can see at a glance why `email` is not
 * editable while `firstName` is.
 */
export interface ResellerProfile {
  /** From portal-api. */
  email: string;
  firstName: string | null;
  lastName: string | null;
  /** Null when portal-api could not report it — rendered as "unknown", never
   * as "off", because telling someone their second factor is disabled when it
   * is not is the more dangerous of the two wrong answers. */
  twoFactorEnabled: boolean | null;
  lastSignInAt: string | null;
  /** From crm-api's live membership. Display only — every gate that matters is
   * enforced server-side against the same membership (spec D §6.1). */
  role: ResellerRoleKey;
  /** crm-api's `membership_role_label`; absent from an older crm-api. */
  roleLabel?: string;
  resellerId: number;
  resellerName: string;
}

/** What the profile form may change. Email is absent deliberately: it is the
 * join key crm-api resolves a membership on. */
export interface ProfileInput {
  firstName?: string;
  lastName?: string;
}

/** One person on the reseller's own roster. */
export interface TeamMember {
  id: number;
  email: string;
  role: ResellerRoleKey;
  /** crm-api's `role_label` — the only source of a custom role's name on a
   * roster row. Absent from an older crm-api. */
  roleLabel?: string;
  active: boolean;
  createdAt: string | null;
  lastSeenAt: string | null;
  invitedBy: string | null;
  /** True for the row describing the viewer. The portal labels it "you" and
   * leaves off the controls the server would refuse anyway. */
  self: boolean;
  permissionGrants: ResellerPermission[];
  permissionDenies: ResellerPermission[];
  effectivePermissions: ResellerPermission[];
}

export interface TeamRoster {
  members: TeamMember[];
  /** The viewer's LIVE role, from crm-api — not from the session cookie, which
   * is minted at sign-in and stale for hours after a demotion. */
  viewer: { email: string; role: ResellerRoleKey; roleLabel?: string; permissions: ResellerPermission[] };
}

/** One node in the reseller network. */
export interface NetworkNode {
  id: number;
  name: string;
  /** The 3-digit invoice-numbering prefix. Allocated by XOP (or, for a
   * self-created child, by crm-api), never chosen. */
  code: string | null;
  parentResellerId: number | null;
  createdAt: string | null;
  customerCount?: number;
  memberCount?: number;
}

/**
 * `GET /api/crm/v1/reseller-network`.
 *
 * `ancestors` are NAMES ONLY and carry no reach — a reseller's data scope is
 * its own subtree, so every ancestor is outside it by definition. The label
 * exists so a partner can see who they sit under, which is a fact they already
 * know from their contract.
 */
export interface ResellerNetwork {
  reseller: NetworkNode;
  /** Nearest first. */
  ancestors: NetworkNode[];
  /** How far below the top of the network this reseller sits. 0 = a direct
   * XOP partner. There is no maximum — nesting is unbounded (spec D §3). */
  depth: number;
  descendants: NetworkNode[];
  viewer: { email: string; role: ResellerRoleKey };
}

/** One row of a company's role matrix. */
export interface RoleMatrixEntry {
  permissions: ResellerPermission[];
  /** True when the company never edited a BUILT-IN role. Always false for a
   * custom role, which has no shipped default to return to. */
  isDefault: boolean;
  /** A role the company created (2026-09-15): renameable and deletable,
   * where a built-in can only be reset. */
  custom: boolean;
  /** The display name — crm-api's for a custom role, the built-in name
   * otherwise. */
  label: string;
}

/** A company's role matrix (spec F §7.1, custom roles 2026-09-15). `roles`
 * is keyed by role key — the three editable built-ins plus any `custom_…`
 * roles; Owner is never a row. `order` is how the rows are listed. */
export interface RoleMatrix {
  roles: Record<string, RoleMatrixEntry>;
  order: string[];
}

/** crm-api's `GET /api/v1/reseller-permissions/catalog`, as served. */
export interface PermissionCatalog {
  sections: Record<string, string>;
  verbs: string[];
  keys: string[];
  grantable: string[];
  implications: Record<string, string[]>;
  roles: string[];
  editable_roles: string[];
  defaults: Record<string, string[]>;
}
